import React, { useEffect, useState, useCallback, memo, useRef } from "react";
import PropTypes from "prop-types";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Avatar, Typography, TextField, Button, CircularProgress, Divider
} from "@mui/material";
import axios from "axios";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import { decryptText } from "config/encryption";
import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MDButton from "components/MDButton";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SendIcon from '@mui/icons-material/Send';

const ReplyList = memo(({ replies, loading, expanded, onToggle, currentUserId, handleEditReply, editingReplyId, editingReplyText, setEditingReplyText, handleSaveEdit, handleCancelEdit, editingReplyLoading }) => {
    if (loading) return <Box display="flex" justifyContent="center" mt={1}><CircularProgress size={18} /></Box>;
    if (!replies || replies.length === 0) return null;
    return (
        <Box mt={1} ml={3} borderLeft="2px solid #eee" pl={2}>
            {replies.map(reply => (
                <Box key={reply.replyId} display="flex" alignItems="flex-start" mb={1}>
                    <Avatar
                        src={getUploadUrl(reply.img)}
                        sx={{ width: 28, height: 28, mr: 1 }}
                    >
                        {reply.repliedByName ? reply.repliedByName[0] : "?"}
                    </Avatar>
                    <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography fontWeight={600} fontSize={13}>{reply.repliedByName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(reply.repliedAt).toLocaleString()}
                            </Typography>
                            {reply.repliedByStudentId === currentUserId && editingReplyId !== reply.replyId && (
                                <IconButton size="small" sx={{ ml: 1 }} onClick={() => handleEditReply(reply)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                        {editingReplyId === reply.replyId ? (
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={editingReplyText}
                                    onChange={e => setEditingReplyText(e.target.value)}
                                    sx={{ background: '#f7f9fb', borderRadius: 2 }}
                                    InputProps={{ disableUnderline: true }}
                                />
                                <MDButton
                                    size="small"
                                    variant="contained"
                                    color="info"
                                    onClick={() => handleSaveEdit(reply)}
                                    disabled={editingReplyLoading || !editingReplyText.trim()}
                                    sx={{ minWidth: 50, fontSize: 12, height: 28, px: 1.5, borderRadius: 2 }}
                                >
                                    Save
                                </MDButton>
                                <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancelEdit}
                                    sx={{ minWidth: 50, fontSize: 12, height: 28, px: 1.5, borderRadius: 2 }}
                                >
                                    Cancel
                                </MDButton>
                            </Box>
                        ) : (
                            <Typography fontSize={12} color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                {reply.replyText}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
            {onToggle && (
                <Tooltip title={expanded ? "Hide replies" : "View all replies"}>
                    <IconButton
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5 }}
                        onClick={onToggle}
                    >
                        {expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
});

ReplyList.propTypes = {
    replies: PropTypes.array,
    loading: PropTypes.bool,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    currentUserId: PropTypes.number,
    handleEditReply: PropTypes.func,
    editingReplyId: PropTypes.number,
    editingReplyText: PropTypes.string,
    setEditingReplyText: PropTypes.func,
    handleSaveEdit: PropTypes.func,
    handleCancelEdit: PropTypes.func,
    editingReplyLoading: PropTypes.bool,
};

const CareerComments = ({ careerId, open, onClose }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [posting, setPosting] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null); // commentId
    const [replyText, setReplyText] = useState("");
    const [postingReply, setPostingReply] = useState(false);
    const [expandedReplies, setExpandedReplies] = useState({}); // { [commentId]: true/false }
    const [repliesLoading, setRepliesLoading] = useState({}); // { [commentId]: true/false }
    const [repliesCache, setRepliesCache] = useState({}); // { [commentId]: [replies] }
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [editingReplyText, setEditingReplyText] = useState("");
    const [editingReplyLoading, setEditingReplyLoading] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const inputRef = useRef();

    // Fetch all comments with only the most recent reply for each
    const fetchComments = useCallback(async () => {
        if (!careerId) return;
        setLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.PROFILE.GET_CAREER_COMMENTS_WITH_REPLIES(careerId));
            // For each comment, only keep the most recent reply in replies array
            const commentsWithOneReply = response.data.map(comment => ({
                ...comment,
                replies: Array.isArray(comment.replies) && comment.replies.length > 0
                    ? [comment.replies[comment.replies.length - 1]]
                    : []
            }));
            setComments(commentsWithOneReply);
            // Cache the full replies for each comment (if already loaded)
            setRepliesCache({});
            setExpandedReplies({});
        } catch (error) {
            setComments([]);
        } finally {
            setLoading(false);
        }
    }, [careerId]);

    // Fetch all replies for a comment when expanded
    const fetchAllReplies = async (commentId) => {
        setRepliesLoading(prev => ({ ...prev, [commentId]: true }));
        try {
            // Fetch the full comment with all replies
            const response = await axios.get(API_ENDPOINTS.PROFILE.GET_CAREER_COMMENTS_WITH_REPLIES(careerId));
            const comment = response.data.find(c => c.commentId === commentId);
            if (comment && Array.isArray(comment.replies)) {
                setRepliesCache(prev => ({ ...prev, [commentId]: comment.replies }));
                setExpandedReplies(prev => ({ ...prev, [commentId]: true }));
            }
        } catch (error) {
            // handle error
        } finally {
            setRepliesLoading(prev => ({ ...prev, [commentId]: false }));
        }
    };

    useEffect(() => {
        if (open && careerId) {
            fetchComments();
        }
    }, [open, careerId, fetchComments]);

    useEffect(() => {
        const encryptedData = localStorage.getItem("UserLogDetails");
        if (encryptedData) {
            const decryptedData = decryptText(encryptedData);
            if (decryptedData) {
                const { StudentId } = JSON.parse(decryptedData);
                setCurrentUserId(StudentId);
            }
        }
    }, []);

    // Add a new comment (top-level)
    const handlePost = async () => {
        if (!newComment.trim() || !careerId) return;
        setPosting(true);
        try {
            const encryptedData = localStorage.getItem("UserLogDetails");
            if (!encryptedData) throw new Error("User not logged in");
            const decryptedData = decryptText(encryptedData);
            if (!decryptedData) throw new Error("Failed to decrypt user data");
            const { StudentId } = JSON.parse(decryptedData);

            await axios.post(
                API_ENDPOINTS.PROFILE.ADD_CAREER_COMMENT,
                {
                    careerId: careerId,
                    commentedByStudentId: StudentId,
                    commentText: newComment.trim()
                },
                { headers: { "Content-Type": "application/json" } }
            );
            setNewComment("");
            await fetchComments();
        } catch (error) {
            // handle error
        } finally {
            setPosting(false);
        }
    };

    // Add a reply to a comment
    const handlePostReply = async (commentId) => {
        if (!replyText.trim() || !commentId) return;
        setPostingReply(true);
        try {
            const encryptedData = localStorage.getItem("UserLogDetails");
            if (!encryptedData) throw new Error("User not logged in");
            const decryptedData = decryptText(encryptedData);
            if (!decryptedData) throw new Error("Failed to decrypt user data");
            const { StudentId } = JSON.parse(decryptedData);

            await axios.post(
                API_ENDPOINTS.PROFILE.MERGE_CAREER_COMMENT_REPLY,
                {
                    id: 0,
                    commentId: commentId,
                    repliedByStudentId: StudentId,
                    replyText: replyText.trim()
                },
                { headers: { "Content-Type": "application/json" } }
            );
            setReplyText("");
            setReplyingTo(null);
            await fetchComments();
        } catch (error) {
            // handle error
        } finally {
            setPostingReply(false);
        }
    };

    const handleEditReply = (reply) => {
        setEditingReplyId(reply.replyId);
        setEditingReplyText(reply.replyText);
    };

    const handleCancelEdit = () => {
        setEditingReplyId(null);
        setEditingReplyText("");
    };

    const handleSaveEdit = async (reply) => {
        setEditingReplyLoading(true);
        try {
            await axios.post(
                API_ENDPOINTS.PROFILE.MERGE_CAREER_COMMENT_REPLY,
                {
                    id: reply.replyId,
                    commentId: reply.commentId,
                    repliedByStudentId: currentUserId,
                    replyText: editingReplyText.trim()
                },
                { headers: { "Content-Type": "application/json" } }
            );
            setEditingReplyId(null);
            setEditingReplyText("");
            await fetchComments();
        } catch (error) {
            // handle error
        } finally {
            setEditingReplyLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 0 }}>Comments</DialogTitle>
            <DialogContent dividers sx={{ minHeight: 300, maxHeight: 500, background: '#f7f9fb' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                ) : comments.length === 0 ? (
                    <Typography color="text.secondary" align="center" mt={4}>No comments yet.</Typography>
                ) : (
                    comments.map(comment => (
                        <Box key={comment.commentId} mb={2}>
                            <Box display="flex" alignItems="flex-start" gap={2}>
                                <Avatar
                                    src={getUploadUrl(comment.img)}
                                    sx={{ width: 38, height: 38, boxShadow: 1, border: '2px solid #fff' }}
                                >
                                    {comment.commentedByName ? comment.commentedByName[0] : "?"}
                                </Avatar>
                                <Box flex={1} sx={{ background: '#fff', borderRadius: 3, p: 1.5, boxShadow: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography fontWeight={600} fontSize={14}>{comment.commentedByName}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: 11 }}>
                                            {new Date(comment.commentedAt).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Typography fontSize={13} color="text.primary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                                        {comment.commentText}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                        <MDButton
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                            startIcon={<ReplyIcon fontSize="small" />}
                                            sx={{ minWidth: 60, fontSize: 12, height: 28, px: 1.5, borderRadius: 2, textTransform: 'none' }}
                                            onClick={() => setReplyingTo(replyingTo === comment.commentId ? null : comment.commentId)}
                                        >
                                            Reply
                                        </MDButton>
                                    </Box>
                                    {/* Replies */}
                                    <ReplyList
                                        replies={expandedReplies[comment.commentId] ? (repliesCache[comment.commentId] || []) : comment.replies}
                                        loading={repliesLoading[comment.commentId]}
                                        expanded={!!expandedReplies[comment.commentId]}
                                        onToggle={() => {
                                            if (expandedReplies[comment.commentId]) {
                                                setExpandedReplies(prev => ({ ...prev, [comment.commentId]: false }));
                                            } else {
                                                fetchAllReplies(comment.commentId);
                                            }
                                        }}
                                        currentUserId={currentUserId}
                                        handleEditReply={handleEditReply}
                                        editingReplyId={editingReplyId}
                                        editingReplyText={editingReplyText}
                                        setEditingReplyText={setEditingReplyText}
                                        handleSaveEdit={handleSaveEdit}
                                        handleCancelEdit={handleCancelEdit}
                                        editingReplyLoading={editingReplyLoading}
                                    />
                                    {/* Reply input */}
                                    {replyingTo === comment.commentId && (
                                        <Box mt={1} ml={1.5}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Write a reply..."
                                                value={replyText}
                                                onChange={e => setReplyText(e.target.value)}
                                                multiline
                                                minRows={1}
                                                maxRows={3}
                                                sx={{ mb: 1, background: '#f7f9fb', borderRadius: 2 }}
                                                InputProps={{ disableUnderline: true }}
                                            />
                                            <MDButton
                                                variant="contained"
                                                color="info"
                                                size="small"
                                                onClick={() => handlePostReply(comment.commentId)}
                                                disabled={postingReply || !replyText.trim()}
                                                sx={{ minWidth: 60, boxShadow: 'none', borderRadius: 2 }}
                                            >
                                                {postingReply ? "Posting..." : "Reply"}
                                            </MDButton>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                        </Box>
                    ))
                )}
            </DialogContent>
            <DialogActions sx={{ background: '#f7f9fb', p: 2, pt: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    multiline
                    minRows={1}
                    maxRows={4}
                    variant="filled"
                    sx={{ borderRadius: 8, background: '#fff', flex: 1 }}
                    InputProps={{
                        disableUnderline: true
                    }}
                />
                <IconButton
                    color="info"
                    size="small"
                    onClick={handlePost}
                    disabled={posting || !newComment.trim()}
                    sx={{ ml: 1, borderRadius: 2, background: '#e3f2fd', '&:hover': { background: '#bbdefb' } }}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </DialogActions>
        </Dialog>
    );
};

CareerComments.propTypes = {
    careerId: PropTypes.number,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

CareerComments.defaultProps = {
    careerId: null,
};

export default CareerComments;
